import { forwardRef, useImperativeHandle } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Form, Input } from 'antd';
import { useProcContext } from '@/entities/proc';
import { useNotificationContext } from '@/features/notifications';
import { useUpdateComponent } from '@/shared/api';
import { captureError } from '@/shared/utils/error-reporting';

export const TextAreaEditForm = forwardRef((_, ref) => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotificationContext();
  const { selectedComponent, selectedStep, proc, setSelectedComponent } =
    useProcContext();

  const [form] = Form.useForm();

  const { mutate: updateComponent, isLoading: updateComponentLoading } =
    useUpdateComponent();

  useImperativeHandle(
    ref,
    () => ({
      submit: () => onSubmit(form.getFieldsValue()),
      isLoading: updateComponentLoading,
    }),
    [updateComponentLoading],
  );

  const onSubmit = async ({ label, placeholder, maxLength }: any) => {
    try {
      updateComponent(
        {
          pathParams: { procId: proc?.id!, stepId: selectedStep?.id! },
          body: {
            id: selectedComponent?.id!,
            label,
            placeholder,
            maxLength,
            component: selectedComponent?.component,
            componentName: selectedComponent?.componentName,
          },
        },
        {
          onSuccess: () => {
            setSelectedComponent(undefined);
            queryClient.invalidateQueries({
              queryKey: ['procs', proc?.id],
            });
          },
          onError: (error: any) => {
            showNotification({
              type: 'error',
              message: 'Update error',
              description: error.stack.message,
            });
            captureError(error, {
              module: 'text_area_edit_form_on_error_update_component',
              action: 'update_component',
            });
          },
        },
      );
    } catch (error) {
      captureError(error, {
        module: 'text_area_edit_form_on_error_update_component',
        action: 'update_component',
      });
    }
  };

  return (
    <Form
      form={form}
      name="textAreaEditForm"
      onFinish={onSubmit}
      initialValues={{
        label: selectedComponent?.label,
        placeholder: selectedComponent?.placeholder,
        maxLength: selectedComponent?.maxLength,
      }}
      autoComplete="off"
      layout="vertical"
      style={{ width: '100%' }}
    >
      <Form.Item label="Label" name="label" style={{ width: '100%' }}>
        <Input />
      </Form.Item>
      <Form.Item
        label="Placeholder text"
        name="placeholder"
        style={{ width: '100%' }}
      >
        <Input />
      </Form.Item>
      <Form.Item label="Max length" name="maxLength" style={{ width: '100%' }}>
        <Input type="number" />
      </Form.Item>
    </Form>
  );
});
